import React, { useEffect, useState } from 'react';
import s from './ShoppingCart.module.scss';
import clsx from 'clsx';
import Header from './Header';
import CartItem from './CartItem';
import Subtotal from '../Subtotal';
import useIsAuth from 'sdk/hooks/useIsAuth';
import useGetOrders from 'sdk/hooks/shop/useGetOrders';
import Checkout from '../Checkout';
import Success from '../Checkout/Success';
import Details from '../Checkout/Details';

export enum BasketSteps {
  BASKET = 'basket',
  CHECKOUT = 'checkout',
  SUCCESS = 'success',
  DETAILS = 'details',
}

const ShoppingCart = ({ open, setOpenShoppingCartMenu, defaultStep = BasketSteps.BASKET }) => {
  const { data: isAuthData } = useIsAuth();
  const userId = isAuthData?.user?.id;
  const { data: cartItems, isLoading } = useGetOrders(userId || '');
  const [step, setStep] = useState(defaultStep ?? BasketSteps.BASKET);

  const isBasket = step === BasketSteps.BASKET;
  const isCheckout = step === BasketSteps.CHECKOUT;
  const isSuccess = step === BasketSteps.SUCCESS;
  const isDetails = step === BasketSteps.DETAILS;

  // Group only products and services, hide gift cards completely
  const groupedItems = cartItems?.reduce((acc, item) => {
    if (item.gift_card_id) return acc;

    let key: string | number = item.id;
    let price = item.price ?? 0;

    if (item.product) {
      key = `product_${item.product.id}`;
      price = item.product.price ?? 0;
    } else if (item.service) {
      key = `service_${item.service.id}`;
      price = item.service.service_pricing_duration?.[0]?.price ?? 0;
    }

    if (!acc[key]) {
      acc[key] = { ...item, actions_count: 1, actions_price: price };
    } else {
      acc[key].actions_count += 1;
      acc[key].actions_price += price;
    }

    return acc;
  }, {} as Record<string, any>);

  const uniqueItems = Object.values(groupedItems || {});

  const onClose = () => {
    if (isCheckout || isSuccess || isDetails) {
      setStep(BasketSteps.BASKET);
      return;
    }
    setOpenShoppingCartMenu(true);
  };

  useEffect(() => {
    document.body.style.overflow = open ? 'auto' : 'hidden';
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [open]);

  return (
    <div className={clsx(s.menu, open && s.menu__hidden)}>
      <div className={s.menu__inner}>
        <Header
          text={
            isBasket
              ? 'Shopping cart'
              : isCheckout
              ? `Your order (${uniqueItems.length})`
              : isSuccess
              ? 'Shopping cart'
              : 'Order №123123'
          }
          onClose={onClose}
        />
      </div>

      {isBasket && (
        <>
          <CartItem open={open} data={uniqueItems} />
          <Subtotal data={uniqueItems} setStep={setStep} onClose={onClose} />
        </>
      )}
      {isCheckout && <Checkout data={uniqueItems} cartItems={cartItems} setStep={setStep} />}
      {isSuccess && <Success setStep={setStep} />}
      {isDetails && <Details setOpenShoppingCartMenu={setOpenShoppingCartMenu} />}
    </div>
  );
};

export default ShoppingCart;
